import { NavigateFunction } from "react-router-dom";
import { RecordManagement } from "app/class/record-management";
import SysRecord from "app/class/record";
import recordApi from "app/services/http-services/record/record-api";
import attachmentApi from "app/services/http-services/attachment/attachment-api";
import { axiosInterceptor } from "app/services/http-services/request-interceptor";
import sessionApi from "app/services/http-services/session/session-api";
import tableApi from "app/services/http-services/table/table-api";
import { createRecordMutationCollector } from "shared/services/recordMutationBus";
import type { RecordMutationEvent } from "shared/services/recordMutationBus";
import { modalRuntime } from "modules/record/shared/modal-runtime/modalService";
import type { ModalRuntimeApi } from "modules/record/shared/modal-runtime/modalService";
import {
    appendReturnToParam,
    getCurrentInternalPath,
    getReturnToFromCurrentLocation,
    navigateToReturnTarget,
    type ReturnNavigationOptions,
} from "modules/record/shared/services/recordNavigationReturn";

type ClientActionMode = "form" | "list" | "related_form" | "related_list";

type NotifyFunction = (key: string, message: string) => void;

export interface ClientActionNotificationApi {
    alert: NotifyFunction;
    warning: NotifyFunction;
    success: NotifyFunction;
    info: NotifyFunction;
    modal?: ModalRuntimeApi;
}

export interface ClientActionBaseContext extends ClientActionNotificationApi {
    currentUser?: unknown;
    navigate: NavigateFunction;
}

export interface ExecuteClientActionInput {
    script: string;
    mode: ClientActionMode;
    baseContext: ClientActionBaseContext;
    current?: Record<string, unknown>;
    records?: SysRecord[];
    table?: Record<string, unknown> | null;
    tableName?: string;
    timeoutMs?: number | string | null;
    extraContext?: Record<string, unknown>;
}

export interface ExecuteClientActionResult {
    mutatedTables: string[];
    returnValue?: unknown;
}

type ClientActionHandler = (context: ClientActionContext) => unknown | Promise<unknown>;

interface ClientActionContext {
    mode: ClientActionMode;
    current: Record<string, unknown>;
    records: SysRecord[];
    table: Record<string, unknown>;
    tableName: string;
    user?: unknown;
    client: ReturnType<typeof createClientApi>;
    ui: ClientActionNotificationApi;
    router: {
        navigate: NavigateFunction;
        refresh: () => void;
        currentUrl: string;
        getReturnTo: () => string | null;
        withReturnTo: (target: string, returnTo?: string | null) => string;
        returnTo: (options?: ReturnNavigationOptions) => boolean;
        toRecord: (tableName: string, recordId?: string | null, options?: NavigateRecordOptions) => void;
        toList: (tableName: string, options?: NavigateListOptions) => void;
    };
    utils: {
        now: () => string;
        uuid: () => string;
    };
}

const DEFAULT_TIMEOUT_MS = 10000;
const compiledScripts = new Map<string, ClientActionHandler>();

export const executeClientAction = async ({
    script,
    mode,
    baseContext,
    current = {},
    records = [],
    table = null,
    tableName,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    extraContext = {},
}: ExecuteClientActionInput): Promise<ExecuteClientActionResult> => {
    const resolvedTableName = tableName || String(table?.name || "");
    const handler = compileClientAction(script);
    const mutationCollector = createRecordMutationCollector("client_action");
    const context: ClientActionContext = {
        mode,
        current,
        records,
        table: table || { name: resolvedTableName },
        tableName: resolvedTableName,
        user: baseContext.currentUser,
        client: createClientApi(mutationCollector),
        ui: pickNotifications(baseContext),
        router: createClientRouter(baseContext.navigate),
        utils: {
            now: () => new Date().toISOString(),
            uuid: createUuid,
        },
        ...extraContext,
    };

    const resolvedTimeoutMs = normalizeTimeoutMs(timeoutMs);
    let returnValue: unknown;
    try {
        returnValue = await withTimeout(
            Promise.resolve(handler(context)),
            resolvedTimeoutMs,
            "Client action script timed out"
        );
    } finally {
        mutationCollector.flush();
    }

    return {
        mutatedTables: mutationCollector.touchedTables,
        returnValue,
    };
};

const compileClientAction = (script: string): ClientActionHandler => {
    const cacheKey = hashScript(script);
    const cached = compiledScripts.get(cacheKey);

    if (cached) {
        return cached;
    }

    try {
        // eslint-disable-next-line no-new-func
        const handler = new Function(
            `"use strict";\n${script}\nif (typeof handler !== "function") {\n  throw new Error("Client action script must define a function named handler");\n}\nreturn handler;`
        )() as ClientActionHandler;

        compiledScripts.set(cacheKey, handler);
        return handler;
    } catch (error) {
        throw new Error(`SCRIPT_COMPILATION_ERROR: ${(error as Error).message}`);
    }
};

type RecordMutationCollector = ReturnType<typeof createRecordMutationCollector>;

const createClientApi = (mutationCollector: RecordMutationCollector) => ({
    db: {
        table: (name: string) => createClientDbTable(name, mutationCollector),
    },
    cache: {
        invalidateRecords: (input: string | Array<string | RecordMutationEvent>) => {
            const events = Array.isArray(input) ? input : [input];
            mutationCollector.markMany(events);
            mutationCollector.flush();
        },
    },
    platformPackage: {
        addRecord: recordApi.addToPlatformPackage,
        commit: recordApi.commitPlatformPackage,
        export: recordApi.exportPlatformPackage,
        convert: recordApi.convertPlatformPackage,
        rollback: recordApi.rollbackPlatformPackage,
    },
    platformJob: {
        run: recordApi.runPlatformJob,
    },
    events: {
        retryDelivery: async (deliveryId: string) => {
            const response = await axiosInterceptor.post(
                "/api/events/deliveries/" + encodeURIComponent(deliveryId) + "/retry"
            );
            mutationCollector.mark({
                table: "sys_event_deliveries",
                operation: "update",
                ids: deliveryId ? [deliveryId] : undefined,
            });
            return response.data?.data || response.data;
        },
    },
    dynamicApi: {
        post: async (path: string, payload?: unknown) => {
            const normalizedPath = String(path || "").replace(/^\/+/, "");
            const response = await axiosInterceptor.post(`/api/ext/${normalizedPath}`, payload || {});
            return response.data?.data || response.data;
        },
    },
    studio: {
        testAiConnection: async (payload?: unknown) => {
            const response = await axiosInterceptor.post("/api/studio/ai/test_connection", payload || {});
            return response.data?.data || response.data;
        },
    },
    email: {
        send: async (payload?: unknown) => {
            const response = await axiosInterceptor.post("/api/ext/platform/email/send", payload || {});
            return response.data?.data || response.data;
        },
        sendTemplate: async (payload?: unknown) => {
            const response = await axiosInterceptor.post("/api/ext/platform/email/send-template", payload || {});
            return response.data?.data || response.data;
        },
        renderTemplate: async (payload?: unknown) => {
            const response = await axiosInterceptor.post("/api/ext/platform/email/render-template", payload || {});
            return response.data?.data || response.data;
        },
        testConnection: async (payload?: unknown) => {
            const response = await axiosInterceptor.post("/api/ext/platform/email/test-connection", payload || {});
            return response.data?.data || response.data;
        },
    },
    attachments: {
        list: async (input: { table: string; recordId: string; column?: string | null }) =>
            attachmentApi.listAttachments(input.table, input.recordId, input.column),
        upload: async (input: { table: string; recordId: string; file: File; column?: string | null }) => {
            const attachment = await attachmentApi.uploadAttachment(input.table, input.recordId, input.file, input.column);
            mutationCollector.mark({ table: input.table, operation: "update", ids: [input.recordId] });
            return attachment;
        },
        delete: async (input: { table: string; recordId: string; attachmentId: string; column?: string | null }) => {
            const attachment = await attachmentApi.deleteAttachment(input.table, input.recordId, input.attachmentId, input.column);
            mutationCollector.mark({ table: input.table, operation: "update", ids: [input.recordId] });
            return attachment;
        },
        downloadUrl: (input: { table: string; recordId: string; attachmentId: string; column?: string | null }) =>
            attachmentApi.downloadAttachmentUrl(input.table, input.recordId, input.attachmentId, input.column),
        viewUrl: (input: { table: string; recordId: string; attachmentId: string; column?: string | null }) =>
            attachmentApi.viewAttachmentUrl(input.table, input.recordId, input.attachmentId, input.column),
        draft: {
            list: async (input: { table: string; draftToken: string; column?: string | null }) =>
                attachmentApi.listDraftAttachments(input.table, input.draftToken, input.column),
            upload: async (input: { table: string; draftToken: string; file: File; column?: string | null }) =>
                attachmentApi.uploadDraftAttachment(input.table, input.draftToken, input.file, input.column),
            delete: async (input: { table: string; draftToken: string; attachmentId: string; column?: string | null }) =>
                attachmentApi.deleteDraftAttachment(input.table, input.draftToken, input.attachmentId, input.column),
            downloadUrl: (input: { table: string; draftToken: string; attachmentId: string; column?: string | null }) =>
                attachmentApi.downloadDraftAttachmentUrl(input.table, input.draftToken, input.attachmentId, input.column),
            viewUrl: (input: { table: string; draftToken: string; attachmentId: string; column?: string | null }) =>
                attachmentApi.viewDraftAttachmentUrl(input.table, input.draftToken, input.attachmentId, input.column),
        },
    },
    session: {
        isAlive: sessionApi.isSessionAlive,
        createUser: async (record: SysRecord) => {
            const response = await sessionApi.postUser(record);
            mutationCollector.mark({ table: "sys_users", operation: "create" });
            return response;
        },
        updateUser: async (record: SysRecord) => {
            const response = await sessionApi.updateUser(record);
            mutationCollector.mark({ table: "sys_users", operation: "update" });
            return response;
        },
        updateUserPassword: async (userId: string, record: SysRecord) => {
            const response = await sessionApi.updateUserPassword(userId, record);
            mutationCollector.mark({ table: "sys_users", operation: "update", ids: userId ? [userId] : undefined });
            return response;
        },
    },
    metadata: {
        createTable: async (table: SysRecord) => {
            const response = await tableApi.postTable(table as any);
            mutationCollector.mark({ table: "sys_tables", operation: "create" });
            return response;
        },
        deleteTable: async (id: string) => {
            const response = await tableApi.deleteTable(id);
            mutationCollector.mark({ table: "sys_tables", operation: "delete", ids: id ? [id] : undefined });
            return response;
        },
        createColumn: async (column: SysRecord) => {
            const response = await tableApi.postColumn(column as any);
            mutationCollector.mark({ table: "sys_columns", operation: "create" });
            return response;
        },
        updateColumn: async (id: string, column: SysRecord) => {
            const response = await tableApi.updateColumn(id, column as any);
            mutationCollector.mark({ table: "sys_columns", operation: "update", ids: id ? [id] : undefined });
            return response;
        },
        deleteColumn: async (id: string) => {
            const response = await tableApi.deleteColumn(id);
            mutationCollector.mark({ table: "sys_columns", operation: "delete", ids: id ? [id] : undefined });
            return response;
        },
createIndex: async (index: SysRecord) => {
    const response = await tableApi.postIndex(index as any);
    mutationCollector.mark({ table: "sys_indexes", operation: "create" });
    return response;
},
deleteIndex: async (id: string) => {
    const response = await tableApi.deleteIndex(id);
    mutationCollector.mark({ table: "sys_indexes", operation: "delete", ids: id ? [id] : undefined });
    return response;
},
buildIndex: async (id: string) => {
    const response = await tableApi.buildIndex(id);
    mutationCollector.mark({ table: "sys_indexes", operation: "update", ids: id ? [id] : undefined });
    return response;
},
    },
});

const createClientDbTable = (tableName: string, mutationCollector: RecordMutationCollector) => ({
    get: (id: string) => new RecordManagement(tableName).get(id),
    first: async (input: { where?: Record<string, unknown>; order?: Array<{ field: string; direction?: "asc" | "desc" }> } = {}) =>
        applyQuery(new RecordManagement(tableName), input).first(),
    count: async (input: { where?: Record<string, unknown> } = {}) => {
        const manager = applyQuery(new RecordManagement(tableName), input);
        manager.paginate(1, 1);

        const response = await manager.run();
        return Number(response.meta?.total ?? response.data.length);
    },
    all: async (input: { where?: Record<string, unknown>; order?: Array<{ field: string; direction?: "asc" | "desc" }>; limit?: number | null } = {}) => {
        const manager = applyQuery(new RecordManagement(tableName), input);

        if (input.limit) {
            manager.limit(input.limit);
        }

        const response = await manager.run();
        return response.data;
    },
    insert: async (attributes: Record<string, unknown>) => {
        const record = await new RecordManagement<SysRecord>(tableName).save(attributes);
        mutationCollector.mark({
            table: tableName,
            operation: "create",
            ids: record?.id ? [record.id] : undefined,
        });
        return record;
    },
    update: async ({ where = {}, attributes = {} }: { where?: Record<string, unknown>; attributes?: Record<string, unknown> } = {}) => {
        const response = await applyQuery(new RecordManagement<SysRecord>(tableName), { where }).run();
        const records = await Promise.all(response.data.map((record) => new RecordManagement<SysRecord>(tableName).update(record.id, attributes)));
        mutationCollector.mark({
            table: tableName,
            operation: "update",
            ids: records.map((record) => record.id).filter((id): id is string => Boolean(id)),
        });
        return records;
    },
    delete: async ({ where = {} }: { where?: Record<string, unknown> } = {}) => {
        const response = await applyQuery(new RecordManagement<SysRecord>(tableName), { where }).run();
        await Promise.all(response.data.map((record) => new RecordManagement(tableName).delete(record.id)));
        const ids = response.data.map((record) => record.id);
        mutationCollector.mark({
            table: tableName,
            operation: "delete",
            ids,
        });
        return ids;
    },
});

const applyQuery = <T>(
    manager: RecordManagement<T>,
    input: { where?: Record<string, unknown>; order?: Array<{ field: string; direction?: "asc" | "desc" }> }
) => {
    Object.entries(input.where || {}).forEach(([field, value]) => {
        manager.where(field, value);
    });

    (input.order || []).forEach((order) => {
        manager.orderBy(order.field, order.direction || "asc");
    });

    return manager;
};

const pickNotifications = (context: ClientActionBaseContext): ClientActionNotificationApi => ({
    alert: context.alert,
    warning: context.warning,
    success: context.success,
    info: context.info,
    modal: modalRuntime,
});

interface NavigateRecordOptions extends ReturnNavigationOptions {
    includeReturnTo?: boolean;
}

interface NavigateListOptions extends ReturnNavigationOptions {
    includeReturnTo?: boolean;
}

const createClientRouter = (navigate: NavigateFunction) => ({
    navigate,
    refresh: () => navigate(0),
    currentUrl: getCurrentInternalPath(),
    getReturnTo: getReturnToFromCurrentLocation,
    withReturnTo: (target: string, returnTo?: string | null) =>
        appendReturnToParam(target, returnTo ?? getCurrentInternalPath()),
    returnTo: (options?: ReturnNavigationOptions) => navigateToReturnTarget(navigate, options),
    toRecord: (tableName: string, recordId?: string | null, options: NavigateRecordOptions = {}) => {
        const { includeReturnTo = false, fallback: _fallback, ...navigateOptions } = options;
        const path = `/home/workspace/${tableName}/record${recordId ? `/${recordId}` : ""}`;
        navigate(includeReturnTo ? appendReturnToParam(path) : path, navigateOptions);
    },
    toList: (tableName: string, options: NavigateListOptions = {}) => {
        const { includeReturnTo = false, fallback: _fallback, ...navigateOptions } = options;
        const path = `/home/workspace/${tableName}/list`;
        navigate(includeReturnTo ? appendReturnToParam(path) : path, navigateOptions);
    },
});

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, message: string) => {
    if (timeoutMs <= 0) {
        return promise;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
        return await Promise.race([
            promise,
            new Promise<T>((_resolve, reject) => {
                timeoutId = setTimeout(() => reject(new Error(`${message} after ${timeoutMs}ms`)), timeoutMs);
            }),
        ]);
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
};

const normalizeTimeoutMs = (value: ExecuteClientActionInput["timeoutMs"]) => {
    if (value === undefined || value === null || value === "") {
        return DEFAULT_TIMEOUT_MS;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (normalizedValue === "") {
        return DEFAULT_TIMEOUT_MS;
    }

    const numericValue = typeof normalizedValue === "number" ? normalizedValue : Number(normalizedValue);

    if (!Number.isFinite(numericValue)) {
        return DEFAULT_TIMEOUT_MS;
    }

    const timeoutMs = Math.trunc(numericValue);

    return timeoutMs < 0 ? DEFAULT_TIMEOUT_MS : timeoutMs;
};

const hashScript = (script: string) => {
    let hash = 0;

    for (let index = 0; index < script.length; index += 1) {
        hash = ((hash << 5) - hash + script.charCodeAt(index)) | 0;
    }

    return `${script.length}:${hash}`;
};

const createUuid = () => {
    if (globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        const value = character === "x" ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
};
